import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { useAuth } from "../../App";

// ── Flag type labels & icons ─────────────────────────────────────────────────
const FLAG_META = {
  // HIGH severity
  duplicate_expense:  { label: "Duplicate Expense",      icon: "🔁", color: "#DC2626" },
  image_duplicate:    { label: "Duplicate Bill Image",   icon: "🖼️", color: "#DC2626" },
  out_of_window:      { label: "Outside Trip Window",    icon: "📅", color: "#DC2626" },
  threshold_split:    { label: "Threshold Splitting",    icon: "✂️", color: "#DC2626" },
  // MEDIUM severity
  round_number:       { label: "Round Number Bill",      icon: "🔵", color: "#D97706" },
  weekend_claim:      { label: "Weekend/Holiday Claim",  icon: "📆", color: "#D97706" },
  late_submission:    { label: "Late Submission",        icon: "⏰", color: "#D97706" },
  cash_ratio:         { label: "High Cash Ratio",        icon: "💵", color: "#D97706" },
  amount_anomaly:     { label: "Unusual Amount",         icon: "📈", color: "#D97706" },
  hotel_overlap:      { label: "Hotel Date Overlap",     icon: "🏨", color: "#D97706" },
  dual_employee_meal: { label: "Dual Employee Meal",     icon: "🍽️", color: "#D97706" },
  advance_no_expense: { label: "Advance — No Expenses",  icon: "💰", color: "#D97706" },
  spend_spike:        { label: "Monthly Spend Spike",    icon: "📊", color: "#D97706" },
  // LOW severity
  vague_description:  { label: "Vague Description",      icon: "📝", color: "#6B7280" },
  minor_cash:         { label: "Petty Cash Entry",       icon: "🪙", color: "#6B7280" },
};

const SEV_STYLE = {
  high:   { bg: "#FEE2E2", color: "#DC2626", label: "HIGH" },
  medium: { bg: "#FEF3C7", color: "#D97706", label: "MED"  },
  low:    { bg: "#DCFCE7", color: "#16A34A", label: "LOW"  },
};

const STATUS_STYLE = {
  open:             { bg: "#FEF2F2", color: "#DC2626",  border: "#FCA5A5", label: "Open"           },
  manager_reviewed: { bg: "#EFF6FF", color: "#1D4ED8",  border: "#BFDBFE", label: "Mgr Responded"  },
  reviewed:         { bg: "#F0FDF4", color: "#16A34A",  border: "#86EFAC", label: "Reviewed"       },
  escalated:        { bg: "#FFF7ED", color: "#EA580C",  border: "#FED7AA", label: "Escalated"      },
  false_positive:   { bg: "#F5F3FF", color: "#7C3AED",  border: "#C4B5FD", label: "False Positive" },
};

const COMPANY_ACCENT = { ebek: "#E31E24", ebekmed: "#2AB5A5", all: "#7C3AED" };

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Summary Bar
// ─────────────────────────────────────────────────────────────────────────────
function SummaryBar({ summary, activeTab, onTab }) {
  const cards = [
    { key: "all",             label: "All Flags",     total: summary.total,                          icon: "🔍", color: "#374151" },
    { key: "open",            label: "Open",          total: summary.open?.total || 0,               icon: "🔴", color: "#DC2626" },
    { key: "manager_reviewed",label: "Mgr Responded", total: summary.manager_reviewed?.total || 0,   icon: "📝", color: "#1D4ED8" },
    { key: "escalated",       label: "Escalated",     total: summary.escalated?.total || 0,          icon: "🟠", color: "#EA580C" },
    { key: "reviewed",        label: "Reviewed",      total: summary.reviewed?.total || 0,           icon: "✅", color: "#16A34A" },
    { key: "false_positive",  label: "False Positive",total: summary.false_positive?.total || 0,     icon: "🟣", color: "#7C3AED" },
  ];

  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
      {cards.map(card => (
        <button
          key={card.key}
          onClick={() => onTab(card.key)}
          style={{
            flex: "1 1 140px",
            padding: "14px 20px",
            background: activeTab === card.key ? card.color : "#fff",
            color:      activeTab === card.key ? "#fff"      : card.color,
            border: `2px solid ${card.color}`,
            borderRadius: 12,
            cursor: "pointer",
            textAlign: "left",
            transition: "all .15s",
            boxShadow: activeTab === card.key ? `0 4px 12px ${card.color}40` : "0 1px 3px rgba(0,0,0,.08)",
          }}
        >
          <div style={{ fontSize: 22, marginBottom: 4 }}>{card.icon}</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{card.total}</div>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: .8 }}>{card.label}</div>
          {card.key === "open" && summary.open?.high > 0 && (
            <div style={{ fontSize: 11, marginTop: 4, background: "rgba(255,255,255,.25)", borderRadius: 6, padding: "2px 6px", display: "inline-block" }}>
              {summary.open.high} HIGH
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Filter Bar
// ─────────────────────────────────────────────────────────────────────────────
function FilterBar({ filters, onChange, employees, userRole, accentColor }) {
  return (
    <div style={{
      display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
      background: "#f9fafb", border: "1px solid #e5e7eb",
      borderRadius: 10, padding: "14px 16px", marginBottom: 20,
    }}>
      <div style={{ flex: "1 1 140px" }}>
        <label style={labelStyle}>Severity</label>
        <select value={filters.severity} onChange={e => onChange("severity", e.target.value)} style={selectStyle}>
          <option value="all">All Severities</option>
          <option value="high">🔴 High</option>
          <option value="medium">🟡 Medium</option>
          <option value="low">🟢 Low</option>
        </select>
      </div>
      <div style={{ flex: "1 1 160px" }}>
        <label style={labelStyle}>Employee</label>
        <select value={filters.employee_id} onChange={e => onChange("employee_id", e.target.value)} style={selectStyle}>
          <option value="">All Employees</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
      </div>
      <div style={{ flex: "1 1 130px" }}>
        <label style={labelStyle}>From Date</label>
        <input type="date" value={filters.from} onChange={e => onChange("from", e.target.value)} style={inputStyle} />
      </div>
      <div style={{ flex: "1 1 130px" }}>
        <label style={labelStyle}>To Date</label>
        <input type="date" value={filters.to} onChange={e => onChange("to", e.target.value)} style={inputStyle} />
      </div>
      {(userRole === "superadmin" || userRole === "executive") && (
        <div style={{ flex: "1 1 130px" }}>
          <label style={labelStyle}>Company</label>
          <select value={filters.company} onChange={e => onChange("company", e.target.value)} style={selectStyle}>
            <option value="all">All Companies</option>
            <option value="ebek">Ebek</option>
            <option value="ebekmed">Ebek Med</option>
          </select>
        </div>
      )}
      <button
        onClick={() => onChange("__reset__")}
        style={{ padding: "8px 14px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#6b7280", alignSelf: "flex-end" }}
      >
        ✕ Reset
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Action Modal
// ─────────────────────────────────────────────────────────────────────────────
function ActionModal({ flag, onClose, onSubmit, accentColor }) {
  const [action, setAction] = useState("reviewed");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  if (!flag) return null;

  const fm = FLAG_META[flag.flag_type] || { label: flag.flag_type, icon: "⚠️" };
  const sev = SEV_STYLE[flag.severity] || SEV_STYLE.medium;

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit(flag.id, action, notes);
    setLoading(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 28,
        width: "100%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,.2)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 28 }}>{fm.icon}</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{fm.label}</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {flag.employee_name} · {flag.trip_title || "—"}
            </div>
          </div>
          <span style={{ marginLeft: "auto", padding: "3px 10px", background: sev.bg, color: sev.color, borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
            {sev.label}
          </span>
        </div>

        {/* Description */}
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 14px", marginBottom: 20, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
          {flag.description}
        </div>

        {/* Action Selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...labelStyle, fontSize: 13 }}>Take Action</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { key: "reviewed",       icon: "✅", label: "Mark Reviewed",   color: "#16A34A" },
              { key: "escalated",      icon: "🔺", label: "Escalate",        color: "#EA580C" },
              { key: "false_positive", icon: "🟣", label: "False Positive",  color: "#7C3AED" },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setAction(opt.key)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: action === opt.key ? opt.color : "#fff",
                  color:      action === opt.key ? "#fff"      : opt.color,
                  border: `2px solid ${opt.color}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  transition: "all .15s",
                }}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...labelStyle, fontSize: 13 }}>
            Review Notes {action === "escalated" ? "(required for escalation)" : "(optional)"}
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={
              action === "reviewed"       ? "e.g. Verified with employee, expense is legitimate..." :
              action === "escalated"      ? "e.g. Escalating to Finance Head — potential fraud..." :
              "e.g. Employee travels on weekends regularly, pattern is normal..."
            }
            rows={3}
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }}
          />
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || (action === "escalated" && !notes.trim())}
            style={{
              padding: "10px 24px",
              background: action === "reviewed" ? "#16A34A" : action === "escalated" ? "#EA580C" : "#7C3AED",
              color: "#fff", border: "none", borderRadius: 8,
              cursor: "pointer", fontWeight: 700, fontSize: 14,
              opacity: (loading || (action === "escalated" && !notes.trim())) ? .5 : 1,
            }}
          >
            {loading ? "Saving…" : `Confirm — ${action === "reviewed" ? "Mark Reviewed" : action === "escalated" ? "Escalate" : "False Positive"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Manager Response Modal
// Managers use this to formally respond to an audit flag after reviewing
// with the employee. Sends POST /api/audit/flags/:id/manager-response
// ─────────────────────────────────────────────────────────────────────────────
function ManagerResponseModal({ flag, onClose, onSubmit, accentColor }) {
  const [verified,     setVerified]     = useState(flag.manager_verified     || false);
  const [explanation,  setExplanation]  = useState(flag.manager_explanation  || "legitimate");
  const [justification,setJustification]= useState(flag.manager_justification|| "");
  const [actionTaken,  setActionTaken]  = useState(flag.manager_action_taken || "no_action");
  const [escalate,     setEscalate]     = useState(false);
  const [loading,      setLoading]      = useState(false);

  const fm  = FLAG_META[flag.flag_type] || { label: flag.flag_type, icon: "⚠️" };
  const sev = SEV_STYLE[flag.severity]  || SEV_STYLE.medium;

  const isEditing = flag.status === "manager_reviewed";

  const handleSubmit = async () => {
    if (!justification.trim()) {
      toast.error("Justification is required");
      return;
    }
    setLoading(true);
    await onSubmit(flag.id, { verified, explanation, justification: justification.trim(), action_taken: actionTaken, escalate });
    setLoading(false);
  };

  const willEscalate = escalate || explanation === "suspicious";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 28,
        width: "100%", maxWidth: 560,
        boxShadow: "0 20px 60px rgba(0,0,0,.22)",
        maxHeight: "90vh", overflowY: "auto",
      }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 28 }}>{fm.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
              {isEditing ? "Update Manager Response" : "Manager Response"} — {fm.label}
            </div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {flag.employee_name} · {flag.trip_title || "—"}
            </div>
          </div>
          <span style={{ padding: "3px 10px", background: sev.bg, color: sev.color, borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
            {sev.label}
          </span>
        </div>

        {isEditing && (
          <div style={{ marginBottom: 16, padding: "6px 10px", background: "#EFF6FF", borderRadius: 7, fontSize: 12, color: "#1D4ED8", fontWeight: 600 }}>
            📝 You already responded to this flag. You can update your response below.
          </div>
        )}

        {/* Flag description */}
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 14px", marginBottom: 20, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
          {flag.description}
        </div>

        {/* Step 1 — Verified with employee */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18,
          cursor: "pointer", padding: "12px 14px",
          background: verified ? "#F0FDF4" : "#f9fafb",
          borderRadius: 8, border: `1px solid ${verified ? "#86EFAC" : "#e5e7eb"}`,
        }}>
          <input
            type="checkbox"
            checked={verified}
            onChange={e => setVerified(e.target.checked)}
            style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer" }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: verified ? "#15803D" : "#111827" }}>
              ✅ I have discussed this finding with the employee
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Confirm you've reviewed the flagged expense with the employee directly before submitting
            </div>
          </div>
        </label>

        {/* Step 2 — Explanation category */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            Explanation Category <span style={{ color: "#DC2626" }}>*</span>
          </label>
          <select value={explanation} onChange={e => setExplanation(e.target.value)} style={selectStyle}>
            <option value="legitimate">✅ Legitimate — Expense is valid and justified</option>
            <option value="employee_error">📝 Employee Error — Mistake made by the employee</option>
            <option value="policy_exception">⚡ Policy Exception — Valid but outside standard policy</option>
            <option value="suspicious">🚨 Suspicious — Requires further investigation</option>
          </select>
          {explanation === "suspicious" && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: "#FEF2F2", borderRadius: 7, fontSize: 12, color: "#DC2626", fontWeight: 600, display: "flex", gap: 6 }}>
              <span>⚠️</span>
              <span>Selecting "Suspicious" will automatically escalate this flag to Admin / Finance for further review.</span>
            </div>
          )}
        </div>

        {/* Step 3 — Justification */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            Justification <span style={{ color: "#DC2626" }}>*</span>
          </label>
          <textarea
            value={justification}
            onChange={e => setJustification(e.target.value)}
            placeholder="Provide a detailed narrative of your findings after reviewing with the employee. Include any relevant context, reasons for the expense, and why you consider it legitimate or otherwise…"
            rows={4}
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }}
          />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            {justification.trim().length} characters · Min ~20 recommended
          </div>
        </div>

        {/* Step 4 — Action taken */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Action Taken</label>
          <select value={actionTaken} onChange={e => setActionTaken(e.target.value)} style={selectStyle}>
            <option value="no_action">No Action Required</option>
            <option value="requested_bills">Requested Supporting Bills from Employee</option>
            <option value="expense_adjusted">Expense Amount Adjusted</option>
            <option value="further_review">Flagged for Further Review</option>
          </select>
        </div>

        {/* Step 5 — Manual escalate toggle (only if not already suspicious) */}
        {explanation !== "suspicious" && (
          <label style={{
            display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20,
            cursor: "pointer", padding: "12px 14px",
            background: escalate ? "#FFF7ED" : "#f9fafb",
            borderRadius: 8, border: `1px solid ${escalate ? "#FED7AA" : "#e5e7eb"}`,
          }}>
            <input
              type="checkbox"
              checked={escalate}
              onChange={e => setEscalate(e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer" }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: escalate ? "#EA580C" : "#111827" }}>
                🔺 Escalate to Admin / Finance
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Flag this for immediate attention — use when you need Finance or Admin to make the final call
              </div>
            </div>
          </label>
        )}

        {/* Footer */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{ padding: "10px 20px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !justification.trim()}
            style={{
              padding: "10px 24px",
              background: willEscalate ? "#EA580C" : accentColor,
              color: "#fff", border: "none", borderRadius: 8,
              cursor: (loading || !justification.trim()) ? "not-allowed" : "pointer",
              fontWeight: 700, fontSize: 14,
              opacity: (loading || !justification.trim()) ? .5 : 1,
            }}
          >
            {loading ? "Submitting…" : willEscalate ? "🔺 Submit & Escalate" : isEditing ? "✏️ Update Response" : "📝 Submit Response"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Flag Card
// ─────────────────────────────────────────────────────────────────────────────
function FlagCard({ flag, onAction, onReopen, onManagerRespond, user, accentColor }) {
  const [expanded, setExpanded] = useState(false);
  const fm = FLAG_META[flag.flag_type] || { label: flag.flag_type, icon: "⚠️", color: "#6B7280" };
  const sev = SEV_STYLE[flag.severity] || SEV_STYLE.medium;
  const sts = STATUS_STYLE[flag.status] || STATUS_STYLE.open;

  const details = typeof flag.details === "string" ? JSON.parse(flag.details || "{}") : (flag.details || {});

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${sts.border}`,
      borderLeft: `4px solid ${sev.color}`,
      borderRadius: 10,
      padding: "14px 16px",
      marginBottom: 10,
      boxShadow: "0 1px 4px rgba(0,0,0,.06)",
    }}>
      {/* Row 1: type + severity + status + employee */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 22, lineHeight: 1 }}>{fm.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{fm.label}</span>
            <span style={{ padding: "2px 8px", background: sev.bg, color: sev.color, borderRadius: 5, fontSize: 11, fontWeight: 800 }}>
              {sev.label}
            </span>
            <span style={{ padding: "2px 8px", background: sts.bg, color: sts.color, border: `1px solid ${sts.border}`, borderRadius: 5, fontSize: 11, fontWeight: 700 }}>
              {sts.label}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "#374151", marginTop: 4, lineHeight: 1.5 }}>
            {flag.description}
          </div>
        </div>
      </div>

      {/* Row 2: meta info */}
      <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap", fontSize: 12, color: "#6b7280" }}>
        <span>👤 <strong style={{ color: "#374151" }}>{flag.employee_name}</strong></span>
        {flag.trip_title && <span>✈️ {flag.trip_title}{flag.trip_destination ? ` — ${flag.trip_destination}` : ""}</span>}
        {flag.expense_category && <span>📂 {flag.expense_category} {flag.expense_amount ? `₹${parseFloat(flag.expense_amount).toFixed(0)}` : ""}</span>}
        <span>🕐 {new Date(flag.detected_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
        {flag.employee_company && (
          <span style={{ padding: "1px 7px", background: flag.employee_company === "ebekmed" ? "#CCFBF1" : "#FEE2E2", color: flag.employee_company === "ebekmed" ? "#0D9488" : "#DC2626", borderRadius: 5, fontWeight: 700 }}>
            {flag.employee_company}
          </span>
        )}
      </div>

      {/* Manager response panel (shown when manager has responded) */}
      {flag.manager_justification && (
        <div style={{
          marginTop: 10, padding: "10px 12px",
          background: "#EFF6FF", borderRadius: 8,
          border: "1px solid #BFDBFE", fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: "#1D4ED8", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            📝 Manager Response
            {flag.manager_response_at && (
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                · {new Date(flag.manager_response_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}
            {flag.manager_verified && (
              <span style={{ padding: "1px 7px", background: "#DCFCE7", color: "#16A34A", borderRadius: 5, fontWeight: 700 }}>✓ Discussed with employee</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
            <span style={{ padding: "2px 8px", background: "#DBEAFE", color: "#1D4ED8", borderRadius: 5, fontWeight: 700 }}>
              {{
                legitimate:       "✅ Legitimate",
                employee_error:   "📝 Employee Error",
                policy_exception: "⚡ Policy Exception",
                suspicious:       "🚨 Suspicious",
              }[flag.manager_explanation] || flag.manager_explanation}
            </span>
            <span style={{ padding: "2px 8px", background: "#F3F4F6", color: "#374151", borderRadius: 5, fontWeight: 600 }}>
              {{
                no_action:        "No Action",
                requested_bills:  "Bills Requested",
                expense_adjusted: "Expense Adjusted",
                further_review:   "Further Review",
              }[flag.manager_action_taken] || flag.manager_action_taken}
            </span>
          </div>
          <div style={{ color: "#374151", lineHeight: 1.5 }}>{flag.manager_justification}</div>
        </div>
      )}

      {/* Admin/superadmin final review info (if reviewed / escalated / false_positive by admin) */}
      {flag.status !== "open" && flag.status !== "manager_reviewed" && flag.reviewed_by_name && (
        <div style={{ marginTop: 8, padding: "8px 10px", background: sts.bg, borderRadius: 6, fontSize: 12, color: sts.color }}>
          <strong>{sts.label}</strong> by {flag.reviewed_by_name} on {new Date(flag.reviewed_at).toLocaleDateString("en-IN")}
          {flag.review_notes && <span> · "{flag.review_notes}"</span>}
        </div>
      )}

      {/* Expandable details */}
      {Object.keys(details).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: accentColor, fontWeight: 600, padding: 0 }}
          >
            {expanded ? "▲ Hide details" : "▼ Show details"}
          </button>
          {expanded && (
            <div style={{ marginTop: 8, background: "#f9fafb", borderRadius: 6, padding: "10px 12px", fontSize: 12, fontFamily: "monospace" }}>
              {Object.entries(details).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 3 }}>
                  <span style={{ color: "#6b7280" }}>{k}: </span>
                  <span style={{ color: "#111827", fontWeight: 600 }}>{JSON.stringify(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons — role-aware */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>

        {/* MANAGER view */}
        {user?.role === "manager" && flag.status === "open" && (
          <button
            onClick={() => onManagerRespond(flag)}
            style={{ ...actionBtn, background: "#1D4ED8", color: "#fff" }}
          >
            📝 Respond to Flag
          </button>
        )}
        {user?.role === "manager" && flag.status === "manager_reviewed" && (
          <button
            onClick={() => onManagerRespond(flag)}
            style={{ ...actionBtn, background: "#fff", color: "#1D4ED8", border: "1px solid #1D4ED8" }}
          >
            ✏️ Update Response
          </button>
        )}

        {/* ADMIN / SUPERADMIN / EXECUTIVE view */}
        {user?.role !== "manager" && flag.status === "open" && (
          <>
            <button onClick={() => onAction(flag, "reviewed")}
              style={{ ...actionBtn, background: "#16A34A", color: "#fff" }}>
              ✅ Mark Reviewed
            </button>
            <button onClick={() => onAction(flag, "escalated")}
              style={{ ...actionBtn, background: "#EA580C", color: "#fff" }}>
              🔺 Escalate
            </button>
            <button onClick={() => onAction(flag, "false_positive")}
              style={{ ...actionBtn, background: "#7C3AED", color: "#fff" }}>
              🟣 False Positive
            </button>
          </>
        )}
        {/* Admin can finalize manager_reviewed flags */}
        {user?.role !== "manager" && flag.status === "manager_reviewed" && (
          <>
            <button onClick={() => onAction(flag, "reviewed")}
              style={{ ...actionBtn, background: "#16A34A", color: "#fff" }}>
              ✅ Approve &amp; Close
            </button>
            <button onClick={() => onAction(flag, "escalated")}
              style={{ ...actionBtn, background: "#EA580C", color: "#fff" }}>
              🔺 Escalate
            </button>
            <button onClick={() => onAction(flag, "false_positive")}
              style={{ ...actionBtn, background: "#7C3AED", color: "#fff" }}>
              🟣 False Positive
            </button>
          </>
        )}

        {/* Reopen — admins can reopen any closed flag; managers cannot */}
        {user?.role !== "manager" && !["open", "manager_reviewed"].includes(flag.status) && (
          <button onClick={() => onReopen(flag.id)}
            style={{ ...actionBtn, background: "#fff", color: "#DC2626", border: "1px solid #DC2626" }}>
            ↩ Reopen
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────
const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" };
const selectStyle = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, background: "#fff" };
const inputStyle  = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, background: "#fff", fontFamily: "inherit" };
const actionBtn   = { padding: "7px 14px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700 };

// ─────────────────────────────────────────────────────────────────────────────
// Main AuditDashboard
// ─────────────────────────────────────────────────────────────────────────────
export default function AuditDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const accentColor = COMPANY_ACCENT[user?.company] || "#E31E24";

  const [tab, setTab]         = useState("all");
  const [flags, setFlags]     = useState([]);
  const [summary, setSummary] = useState({ total: 0, open: {}, manager_reviewed: {}, reviewed: {}, escalated: {}, false_positive: {} });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [actionFlag, setActionFlag]           = useState(null); // admin action modal
  const [quickAction, setQuickAction]         = useState(null); // preset action for modal
  const [managerResponseFlag, setManagerResponseFlag] = useState(null); // manager response modal

  const defaultFilters = { severity: "all", employee_id: "", from: "", to: "", company: "all" };
  const [filters, setFilters] = useState(defaultFilters);

  // ── Load summary ─────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await API.get("/audit/flags/summary");
      setSummary(res.data);
    } catch (e) { /* silent */ }
  }, []);

  // ── Load flags ───────────────────────────────────────────────────────────
  const loadFlags = useCallback(async (resetPage = false) => {
    setLoading(true);
    try {
      const p = resetPage ? 1 : page;
      const params = new URLSearchParams({ page: p, limit: 30 });
      if (tab !== "all")              params.set("status",   tab);
      if (filters.severity !== "all") params.set("severity", filters.severity);
      if (filters.employee_id)        params.set("employee_id", filters.employee_id);
      if (filters.from)               params.set("from", filters.from);
      if (filters.to)                 params.set("to",   filters.to);
      if (filters.company !== "all")  params.set("company", filters.company);

      const res = await API.get(`/audit/flags?${params}`);
      setFlags(res.data.flags);
      setTotalPages(res.data.pages);
      setTotalCount(res.data.total);
      if (resetPage) setPage(1);
    } catch (e) {
      console.error("loadFlags error:", e.response?.data || e.message || e);
      toast.error(e.response?.data?.error || e.message || "Failed to load audit flags");
    } finally {
      setLoading(false);
    }
  }, [tab, filters, page]);

  // ── Load employees for filter dropdown ──────────────────────────────────
  useEffect(() => {
    API.get("/users").then(res => setEmployees(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadFlags(true); }, [tab, filters]); // eslint-disable-line
  useEffect(() => { if (page > 1) loadFlags(); }, [page]); // eslint-disable-line

  // ── Handle filter changes ────────────────────────────────────────────────
  const handleFilter = (key, value) => {
    if (key === "__reset__") { setFilters(defaultFilters); return; }
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // ── Submit action ────────────────────────────────────────────────────────
  const handleActionSubmit = async (flagId, action, notes) => {
    try {
      await API.patch(`/audit/flags/${flagId}`, { action, notes });
      toast.success(`Flag marked as ${action === "false_positive" ? "False Positive" : action.charAt(0).toUpperCase() + action.slice(1)}`);
      setActionFlag(null);
      await Promise.all([loadFlags(), loadSummary()]);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update flag");
    }
  };

  // ── Quick action (from card buttons directly) ────────────────────────────
  const handleQuickAction = (flag, action) => {
    setActionFlag(flag);
    setQuickAction(action);
  };

  // ── Reopen a flag ────────────────────────────────────────────────────────
  const handleReopen = async (flagId) => {
    try {
      await API.patch(`/audit/flags/${flagId}`, { action: "open", notes: "" });
      toast.success("Flag reopened");
      await Promise.all([loadFlags(), loadSummary()]);
    } catch (e) {
      toast.error("Failed to reopen flag");
    }
  };

  // ── Manager: submit structured response to a flag ────────────────────────
  const handleManagerResponse = async (flagId, payload) => {
    try {
      await API.post(`/audit/flags/${flagId}/manager-response`, payload);
      const wasEscalated = payload.escalate || payload.explanation === "suspicious";
      toast.success(wasEscalated ? "Flag escalated to Admin / Finance" : "Response submitted successfully");
      setManagerResponseFlag(null);
      await Promise.all([loadFlags(), loadSummary()]);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to submit response");
    }
  };

  const openCount = summary.open?.total || 0;
  const highOpenCount = summary.open?.high || 0;

  return (
    <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            🔍 Audit Dashboard
          </h1>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 14 }}>
            Review flagged expenses before allowing settlement
          </p>
        </div>

        {/* Settlement warning banner */}
        {openCount > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#FEF2F2", border: "1px solid #FCA5A5",
            borderRadius: 10, padding: "10px 16px", fontSize: 13,
          }}>
            <span style={{ fontSize: 20 }}>🚫</span>
            <div>
              <div style={{ fontWeight: 700, color: "#DC2626" }}>Settlement Blocked</div>
              <div style={{ color: "#9B1C1C", fontSize: 12 }}>
                {openCount} open flag{openCount > 1 ? "s" : ""}{highOpenCount > 0 ? ` (${highOpenCount} HIGH severity)` : ""} must be reviewed first
              </div>
            </div>
          </div>
        )}

        {openCount === 0 && summary.total > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#F0FDF4", border: "1px solid #86EFAC",
            borderRadius: 10, padding: "10px 16px", fontSize: 13,
          }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, color: "#16A34A" }}>All Clear</div>
              <div style={{ color: "#166534", fontSize: 12 }}>No open flags — settlements can proceed</div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Tab Bar */}
      <SummaryBar summary={summary} activeTab={tab} onTab={setTab} />

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        onChange={handleFilter}
        employees={employees}
        userRole={user?.role}
        accentColor={accentColor}
      />

      {/* Results Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          {loading ? "Loading…" : `${totalCount} flag${totalCount !== 1 ? "s" : ""} found`}
          {tab !== "all" && (
            <span style={{ marginLeft: 8, padding: "2px 8px", background: "#EFF6FF", color: "#1D4ED8", borderRadius: 5, fontWeight: 700, fontSize: 11 }}>
              {STATUS_STYLE[tab]?.label || tab}
            </span>
          )}
        </div>
        <button
          onClick={() => { loadFlags(); loadSummary(); }}
          style={{ padding: "6px 12px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 7, cursor: "pointer", fontSize: 12, color: "#374151" }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Flag List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <div>Loading audit flags…</div>
        </div>
      ) : flags.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#374151" }}>No flags found</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            {tab === "open"
              ? "No open audit flags — all expenses have been reviewed."
              : "No flags match the current filters."}
          </div>
        </div>
      ) : (
        <>
          {flags.map(flag => (
            <FlagCard
              key={flag.id}
              flag={flag}
              onAction={handleQuickAction}
              onReopen={handleReopen}
              onManagerRespond={setManagerResponseFlag}
              user={user}
              accentColor={accentColor}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: "8px 16px", border: "1px solid #d1d5db", borderRadius: 7, cursor: "pointer", background: page === 1 ? "#f9fafb" : "#fff", color: "#374151" }}
              >
                ← Prev
              </button>
              <span style={{ padding: "8px 16px", fontSize: 13, color: "#6b7280" }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: "8px 16px", border: "1px solid #d1d5db", borderRadius: 7, cursor: "pointer", background: page === totalPages ? "#f9fafb" : "#fff", color: "#374151" }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Admin/Superadmin Action Modal */}
      {actionFlag && (
        <ActionModal
          flag={{ ...actionFlag, _presetAction: quickAction }}
          onClose={() => { setActionFlag(null); setQuickAction(null); }}
          onSubmit={handleActionSubmit}
          accentColor={accentColor}
        />
      )}

      {/* Manager Response Modal */}
      {managerResponseFlag && (
        <ManagerResponseModal
          flag={managerResponseFlag}
          onClose={() => setManagerResponseFlag(null)}
          onSubmit={handleManagerResponse}
          accentColor={accentColor}
        />
      )}
    </div>
  );
}
